/**
 * Client-side debug system implementation.
 * Extends ClientSystem(BaseDebugSystem) so that TypeScript sees all IClientSystem
 * members, while ClientProcess.decorate() injects the actual dispatch methods at runtime.
 */

import {
  BaseDebugSystem,
  type SystemDebugInfo,
  type EntityDebugInfo,
  type SystemDebugUpdateOptions,
  type ReceivedMessage,
  DEBUG_MESSAGES,
  type IDebugPage,
} from '@gotti/core';
import { ClientSystem } from '../IClientSystem.js';
import type { JoinedAreaData } from '../ClientProcess.js';

/**
 * Client debug system configuration options.
 */
export type ClientDebugSystemConfig = {
  /** Whether debug is enabled by default */
  enabled?: boolean;
  /** Maximum logs per system/entity */
  maxLogs?: number;
  /** Whether to show the debug UI by default */
  showUI?: boolean;
  /** Keyboard shortcut to toggle debug UI (default: '`') */
  toggleKey?: string;
};

/**
 * Client-side debug system.
 * Provides debug logging, inspection, and UI for client systems and entities.
 * Receives debug info from the server for unified display.
 *
 * Dispatch methods (dispatchToServer, etc.) are injected at runtime by
 * `ClientProcess.decorate()`. The `ClientSystem()` helper tells TypeScript about
 * them without any per-class `declare` boilerplate.
 */
export class ClientDebugSystem extends ClientSystem(BaseDebugSystem) {
  // -- BaseDebugSystem abstract implementation --
  protected readonly source: 'client' | 'server' = 'client';

  // -- Client-specific properties --
  /** Server area metadata */
  public serverMeta: { areaId?: string; areaType?: string } = {};

  /** Configuration */
  protected readonly config: ClientDebugSystemConfig;

  /** Debug UI instance */
  private debugUI?: DebugUI;

  /** Registered debug pages */
  private readonly registeredPages: Map<string, IDebugPage> = new Map();

  /** Custom page factories */
  private readonly customPageFactories: Map<string, () => IDebugPage> = new Map();

  /** Key handler reference */
  private keyHandler?: (e: KeyboardEvent) => void;

  constructor(config: ClientDebugSystemConfig = {}) {
    super();
    this.config = {
      enabled: true,
      maxLogs: 100,
      showUI: false,
      toggleKey: '`',
      ...config,
    };
    this._enabled = this.config.enabled!;
    this.maxLogs = this.config.maxLogs!;
  }

  // -- Lifecycle hooks --

  /**
   * Called when the system is initialized.
   */
  onInit(): void {
    super.onInit();

    // Set up keyboard shortcut to toggle UI
    if (typeof window !== 'undefined' && this.config.toggleKey) {
      this.keyHandler = (e: KeyboardEvent) => {
        if (e.key === this.config.toggleKey) {
          this.toggleUI();
        }
      };
      window.addEventListener('keydown', this.keyHandler);
    }

    // Initialize UI if showUI is true
    if (this.config.showUI && typeof document !== 'undefined') {
      this.showUI();
    }
  }

  /**
   * Called when the system is stopped.
   */
  onStop(): void {
    if (this.keyHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    this.hideUI();
    this.registeredPages.clear();
  }

  /**
   * Called when the client joins an area.
   */
  onJoinedArea(area: JoinedAreaData, joinedOptions: any): void {
    // Request full debug state from server
    this.dispatchToServer({
      type: DEBUG_MESSAGES.REQUEST_DEBUG_STATE,
      to: this.name,
      data: {},
    });
  }

  // -- Server message handling --

  /**
   * Handle messages from the server debug system.
   */
  onServerMessage(message: ReceivedMessage<any>): void {
    if (!this._enabled) return;

    switch (message.type) {
      case DEBUG_MESSAGES.LOG_ENTRY:
        // Receive log entries from server
        if (Array.isArray(message.data?.logs)) {
          for (const log of message.data.logs) {
            this.addLogEntry({ ...log, source: 'server' });
          }
        }
        break;

      case DEBUG_MESSAGES.SYSTEM_INFO_SYNC:
        // Sync system debug info from server
        if (message.data?.systems) {
          for (const [name, info] of Object.entries(message.data.systems)) {
            this.mergeServerSystemInfo(name, info as SystemDebugInfo);
          }
        }
        break;

      case DEBUG_MESSAGES.ENTITY_INFO_SYNC:
        // Sync entity debug info from server
        if (message.data?.entities) {
          for (const [id, info] of Object.entries(message.data.entities)) {
            this.mergeServerEntityInfo(id, info as EntityDebugInfo);
          }
        }
        break;

      case DEBUG_MESSAGES.FULL_DEBUG_STATE:
        // Full state sync
        if (message.data?.systems) {
          for (const [name, info] of Object.entries(message.data.systems)) {
            this.mergeServerSystemInfo(name, info as SystemDebugInfo);
          }
        }
        if (message.data?.entities) {
          for (const [id, info] of Object.entries(message.data.entities)) {
            this.mergeServerEntityInfo(id, info as EntityDebugInfo);
          }
        }
        if (message.data?.meta) {
          this.serverMeta = message.data.meta;
        }
        break;

      case DEBUG_MESSAGES.AREA_INFO:
        if (message.data?.meta) {
          this.serverMeta = message.data.meta;
        }
        break;
    }
  }

  /**
   * Merge server system info into local storage.
   */
  private mergeServerSystemInfo(systemName: string, serverInfo: SystemDebugInfo): void {
    const existing = this.ensureSystemDebugInfo(systemName);
    
    // Merge server state/properties into by-source maps
    if (serverInfo.state) {
      if (!existing.stateBySource) existing.stateBySource = {};
      existing.stateBySource.server = serverInfo.state;
    }
    if (serverInfo.properties) {
      if (!existing.propertiesBySource) existing.propertiesBySource = {};
      existing.propertiesBySource.server = serverInfo.properties;
    }

    // Merge logs (append server logs)
    if (serverInfo.logs) {
      for (const log of serverInfo.logs) {
        const serverLog = { ...log, source: 'server' as const };
        existing.logs.push(serverLog);
        if (existing.logs.length > existing.maxLogs) {
          existing.logs.shift();
        }
      }
    }
  }

  /**
   * Merge server entity info into local storage.
   */
  private mergeServerEntityInfo(entityId: string, serverInfo: EntityDebugInfo): void {
    const existing = this.ensureEntityDebugInfo(entityId, serverInfo.entityType);
    
    if (serverInfo.name) existing.name = serverInfo.name;
    if (serverInfo.entityType) existing.entityType = serverInfo.entityType;
    if (serverInfo.state) existing.state = { ...existing.state, ...serverInfo.state };
    if (serverInfo.properties) existing.properties = { ...existing.properties, ...serverInfo.properties };

    if (serverInfo.logs) {
      for (const log of serverInfo.logs) {
        const serverLog = { ...log, source: 'server' as const };
        existing.logs.push(serverLog);
        if (existing.logs.length > existing.maxLogs) {
          existing.logs.shift();
        }
      }
    }
  }

  // -- Overrides --

  /**
   * Get area metadata (client has no local area meta, uses server's).
   */
  protected override getAreaMeta(): { areaId?: string; areaType?: string } {
    return this.serverMeta;
  }

  /**
   * Update debug info for a system.
   * Client version auto-categorizes state/properties under stateBySource.client.
   */
  override updateSystemDebugInfo(
    systemName: string,
    info: Partial<Omit<SystemDebugInfo, 'name' | 'logs'>>,
    options?: SystemDebugUpdateOptions
  ): void {
    if (!this._enabled) return;

    const normalizedName = this.normalizeSystemName(systemName);
    const existing = this.ensureSystemDebugInfo(normalizedName);

    if (options?.replace) {
      if (info.state !== undefined) {
        existing.state = this.cloneDebugValue(info.state);
      }
      if (info.properties !== undefined) {
        existing.properties = this.cloneDebugValue(info.properties);
      }
    } else {
      if (info.state !== undefined) {
        existing.state = { ...existing.state, ...this.cloneDebugValue(info.state) };
      }
      if (info.properties !== undefined) {
        existing.properties = { ...existing.properties, ...this.cloneDebugValue(info.properties) };
      }
    }

    // Handle client-side by-source (always 'client' on client side)
    if (info.state !== undefined) {
      if (!existing.stateBySource) existing.stateBySource = {};
      existing.stateBySource.client = options?.replace
        ? this.cloneDebugValue(info.state)
        : { ...existing.stateBySource.client, ...this.cloneDebugValue(info.state) };
    }
    if (info.properties !== undefined) {
      if (!existing.propertiesBySource) existing.propertiesBySource = {};
      existing.propertiesBySource.client = options?.replace
        ? this.cloneDebugValue(info.properties)
        : { ...existing.propertiesBySource.client, ...this.cloneDebugValue(info.properties) };
    }

    if (info.maxLogs !== undefined) {
      existing.maxLogs = info.maxLogs;
    }
  }

  // ========================================
  // Debug UI Methods
  // ========================================

  /**
   * Register a custom debug page.
   */
  registerPage(pageOrFactory: IDebugPage | (() => IDebugPage)): void {
    if (typeof pageOrFactory === 'function') {
      const page = pageOrFactory();
      this.customPageFactories.set(page.config.id, pageOrFactory);
      this.registeredPages.set(page.config.id, page);
    } else {
      this.registeredPages.set(pageOrFactory.config.id, pageOrFactory);
    }
    
    // If UI is already showing, update it
    if (this.debugUI) {
      this.debugUI.refreshPages();
    }
  }

  /**
   * Unregister a custom debug page.
   */
  unregisterPage(pageId: string): void {
    const page = this.registeredPages.get(pageId);
    if (page?.destroy) {
      page.destroy();
    }
    this.registeredPages.delete(pageId);
    this.customPageFactories.delete(pageId);
    
    if (this.debugUI) {
      this.debugUI.refreshPages();
    }
  }

  /**
   * Get all registered pages.
   */
  getRegisteredPages(): IDebugPage[] {
    return Array.from(this.registeredPages.values());
  }

  /**
   * Show the debug UI.
   */
  showUI(): void {
    if (typeof document === 'undefined') return;
    
    if (!this.debugUI) {
      this.debugUI = new DebugUI(this);
    }
    this.debugUI.show();
  }

  /**
   * Hide the debug UI.
   */
  hideUI(): void {
    if (this.debugUI) {
      this.debugUI.hide();
    }
  }

  /**
   * Toggle the debug UI visibility.
   */
  toggleUI(): void {
    if (this.debugUI?.isVisible) {
      this.hideUI();
    } else {
      this.showUI();
    }
  }

  /**
   * Check if debug UI is visible.
   */
  get isUIVisible(): boolean {
    return this.debugUI?.isVisible ?? false;
  }
}

// ========================================
// Debug UI Implementation
// ========================================

/**
 * Debug UI container that renders the debug panel.
 */
class DebugUI {
  private container: HTMLDivElement;
  private tabContainer: HTMLDivElement;
  private pageContainer: HTMLDivElement;
  private toggleButton: HTMLButtonElement;
  private _isVisible: boolean = false;
  private activePage?: IDebugPage;

  constructor(private readonly debugSystem: ClientDebugSystem) {
    // Create main container
    this.container = document.createElement('div');
    this.container.id = 'gotti-debug-ui';
    Object.assign(this.container.style, {
      position: 'fixed',
      top: '0',
      right: '0',
      width: '400px',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      color: '#fff',
      fontFamily: 'monospace',
      fontSize: '12px',
      zIndex: '999999',
      display: 'none',
      flexDirection: 'column',
      boxShadow: '-2px 0 10px rgba(0,0,0,0.5)',
    });

    // Create tab bar
    this.tabContainer = document.createElement('div');
    Object.assign(this.tabContainer.style, {
      display: 'flex',
      flexWrap: 'wrap',
      backgroundColor: '#222',
      padding: '5px',
      borderBottom: '1px solid #444',
    });
    this.container.appendChild(this.tabContainer);

    // Create page container
    this.pageContainer = document.createElement('div');
    Object.assign(this.pageContainer.style, {
      flex: '1',
      overflow: 'auto',
      padding: '10px',
    });
    this.container.appendChild(this.pageContainer);

    // Create toggle button (visible when UI is hidden)
    this.toggleButton = document.createElement('button');
    this.toggleButton.textContent = 'Debug';
    Object.assign(this.toggleButton.style, {
      position: 'fixed',
      top: '10px',
      right: '10px',
      padding: '8px 16px',
      backgroundColor: '#333',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      zIndex: '999998',
      fontFamily: 'monospace',
      fontSize: '12px',
    });
    this.toggleButton.onclick = () => this.debugSystem.toggleUI();

    document.body.appendChild(this.container);
    document.body.appendChild(this.toggleButton);

    this.refreshPages();
  }

  get isVisible(): boolean {
    return this._isVisible;
  }

  show(): void {
    this._isVisible = true;
    this.container.style.display = 'flex';
    this.toggleButton.style.display = 'none';
  }

  hide(): void {
    this._isVisible = false;
    this.container.style.display = 'none';
    this.toggleButton.style.display = 'block';
  }

  refreshPages(): void {
    // Clear tabs
    this.tabContainer.innerHTML = '';

    // Add close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '\u00d7';
    Object.assign(closeBtn.style, {
      padding: '5px 10px',
      backgroundColor: '#c00',
      color: '#fff',
      border: 'none',
      cursor: 'pointer',
      marginRight: '10px',
    });
    closeBtn.onclick = () => this.hide();
    this.tabContainer.appendChild(closeBtn);

    // Get all pages sorted by priority
    const pages = this.debugSystem.getRegisteredPages()
      .sort((a, b) => (a.config.priority ?? 100) - (b.config.priority ?? 100));

    // Create tabs
    for (const page of pages) {
      const tab = document.createElement('button');
      tab.textContent = page.config.label;
      Object.assign(tab.style, {
        padding: '5px 10px',
        backgroundColor: this.activePage === page ? '#555' : '#333',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        marginRight: '2px',
      });
      tab.onclick = () => this.activatePage(page);
      this.tabContainer.appendChild(tab);
    }

    // Activate first page if none active
    if (!this.activePage && pages.length > 0) {
      this.activatePage(pages[0]);
    }
  }

  private activatePage(page: IDebugPage): void {
    // Deactivate current
    if (this.activePage?.onDeactivate) {
      this.activePage.onDeactivate();
    }

    this.activePage = page;

    // Clear page container
    this.pageContainer.innerHTML = '';

    // Create page element
    const element = page.createElement();
    this.pageContainer.appendChild(element);

    // Activate
    if (page.onActivate) {
      page.onActivate();
    }

    // Refresh tab styling
    this.refreshPages();
  }

  destroy(): void {
    if (this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    if (this.toggleButton.parentNode) {
      this.toggleButton.parentNode.removeChild(this.toggleButton);
    }
  }
}
